function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { dataURItoBlob, getTemplate, TranslatableString } from '@rjsf/utils';
import Markdown from 'markdown-to-jsx';

function addNameToDataURL(dataURL, name) {
  if (dataURL === null) {
    return null;
  }

  return dataURL.replace(';base64', ";name=".concat(encodeURIComponent(name), ";base64"));
}

function processFile(file) {
  var name = file.name,
      size = file.size,
      type = file.type;
  return new Promise(function (resolve, reject) {
    var reader = new window.FileReader();
    reader.onerror = reject;

    reader.onload = function (event) {
      var _a;

      if (typeof ((_a = event.target) === null || _a === void 0 ? void 0 : _a.result) === 'string') {
        resolve({
          dataURL: addNameToDataURL(event.target.result, name),
          name: name,
          size: size,
          type: type
        });
      } else {
        resolve({
          dataURL: null,
          name: name,
          size: size,
          type: type
        });
      }
    };

    reader.readAsDataURL(file);
  });
}

function processFiles(files) {
  return Promise.all(Array.from(files).map(processFile));
}

function FileInfoPreview(_ref) {
  var fileInfo = _ref.fileInfo,
      registry = _ref.registry;
  var translateString = registry.translateString;
  var dataURL = fileInfo.dataURL,
      type = fileInfo.type,
      name = fileInfo.name;

  if (!dataURL) {
    return null;
  } // If type is JPEG or PNG then show image preview.
  // Originally, any type of image was supported, but this was changed into a whitelist
  // since SVGs and animated GIFs are also images, which are generally considered a security risk.


  if (['image/jpeg', 'image/png'].includes(type)) {
    return _jsx("img", {
      src: dataURL,
      style: {
        maxWidth: '100%'
      },
      className: 'file-preview'
    });
  } // otherwise, let users download file


  return _jsxs(_Fragment, {
    children: [' ', _jsx("a", {
      download: "preview-".concat(name),
      href: dataURL,
      className: 'file-download',
      children: translateString(TranslatableString.PreviewLabel)
    })]
  });
}

function FilesInfo(_ref2) {
  var filesInfo = _ref2.filesInfo,
      registry = _ref2.registry,
      preview = _ref2.preview,
      onRemove = _ref2.onRemove,
      options = _ref2.options;

  if (filesInfo.length === 0) {
    return null;
  }

  var translateString = registry.translateString;

  var _getTemplate = getTemplate('ButtonTemplates', registry, options),
      RemoveButton = _getTemplate.RemoveButton;

  return _jsx("ul", {
    className: 'file-info',
    children: filesInfo.map(function (fileInfo, key) {
      var name = fileInfo.name,
          size = fileInfo.size,
          type = fileInfo.type;

      var handleRemove = function handleRemove() {
        return onRemove(key);
      };

      return _jsxs("li", {
        children: [_jsx(Markdown, {
          children: translateString(TranslatableString.FilesInfo, [name, type, String(size)])
        }), preview && _jsx(FileInfoPreview, {
          fileInfo: fileInfo,
          registry: registry
        }), _jsx(RemoveButton, {
          onClick: handleRemove,
          registry: registry
        })]
      }, key);
    })
  });
}

function extractFileInfo(dataURLs) {
  return dataURLs.reduce(function (acc, dataURL) {
    if (!dataURL) {
      return acc;
    }

    try {
      var _dataURItoBlob = dataURItoBlob(dataURL),
          blob = _dataURItoBlob.blob,
          name = _dataURItoBlob.name;

      return [].concat(_toConsumableArray(acc), [{
        dataURL: dataURL,
        name: name,
        size: blob.size,
        type: blob.type
      }]);
    } catch (e) {
      // Invalid dataURI, so just ignore it.
      return acc;
    }
  }, []);
}
/**
 *  The `FileWidget` is a widget for rendering file upload fields.
 *  It is typically used with a string property with data-url format.
 */


function FileWidget(props) {
  var disabled = props.disabled,
      readonly = props.readonly,
      required = props.required,
      multiple = props.multiple,
      onChange = props.onChange,
      value = props.value,
      options = props.options,
      registry = props.registry;
  var BaseInputTemplate = getTemplate('BaseInputTemplate', registry, options);
  var handleChange = useCallback(function (event) {
    if (!event.target.files) {
      return;
    } // Due to variances in themes, dealing with multiple files for the array case now happens one file at a time.
    // This is because we don't pass `multiple` into the `BaseInputTemplate` anymore. Instead, we deal with the single
    // file in each event and concatenate them together ourselves


    processFiles(event.target.files).then(function (filesInfoEvent) {
      var newValue = filesInfoEvent.map(function (fileInfo) {
        return fileInfo.dataURL;
      });

      if (multiple) {
        onChange(value.concat(newValue));
      } else {
        onChange(newValue[0]);
      }
    });
  }, [multiple, value, onChange]);
  var filesInfo = useMemo(function () {
    return extractFileInfo(Array.isArray(value) ? value : [value]);
  }, [value]);
  var rmFile = useCallback(function (index) {
    if (multiple) {
      var newValue = value.filter(function (_, i) {
        return i !== index;
      });
      onChange(newValue);
    } else {
      onChange(undefined);
    }
  }, [multiple, value, onChange]);
  return _jsxs("div", {
    children: [_jsx(BaseInputTemplate, _objectSpread(_objectSpread({}, props), {}, {
      disabled: disabled || readonly,
      type: 'file',
      required: value ? false : required,
      onChangeOverride: handleChange,
      value: '',
      accept: options.accept ? String(options.accept) : undefined
    })), _jsx(FilesInfo, {
      filesInfo: filesInfo,
      onRemove: rmFile,
      registry: registry,
      preview: options.filePreview,
      options: options
    })]
  });
}

export default FileWidget;